import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { 
  Database, 
  Download, 
  Upload, 
  Trash2, 
  RefreshCw, 
  Plus, 
  AlertTriangle,
  CheckCircle,
  Loader2,
  HardDrive,
  History
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BackupRestore = () => {
  const { user } = useAuth();
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  
  // Dialogs
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);

  useEffect(() => {
    fetchBackups();
  }, []);

  const fetchBackups = async () => {
    try {
      const response = await axios.get(`${API}/admin/backups`);
      setBackups(response.data || []);
    } catch (error) {
      console.error('Error fetching backups:', error);
      toast.error('Failed to load backups');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    setCreating(true);
    try {
      const response = await axios.post(`${API}/admin/backups`);
      toast.success('Backup created successfully!');
      fetchBackups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create backup');
    } finally {
      setCreating(false);
    }
  };

  const handleDownloadBackup = async (backupId) => {
    try {
      const response = await axios.get(`${API}/admin/backups/${backupId}/download`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${backupId}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup downloaded!');
    } catch (error) {
      toast.error('Failed to download backup');
    }
  };

  const handleRestoreBackup = async () => {
    if (!selectedBackup) return;
    
    setRestoring(true);
    try {
      const response = await axios.post(`${API}/admin/backups/restore/${selectedBackup.id}`);
      toast.success('Backup restored successfully!');
      setShowRestoreDialog(false);
      setSelectedBackup(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore backup');
    } finally {
      setRestoring(false);
    }
  };

  const handleDeleteBackup = async () => {
    if (!selectedBackup) return;
    
    setDeleting(selectedBackup.id);
    try {
      await axios.delete(`${API}/admin/backups/${selectedBackup.id}`);
      toast.success('Backup deleted!');
      setShowDeleteDialog(false);
      setSelectedBackup(null);
      fetchBackups();
    } catch (error) {
      toast.error('Failed to delete backup');
    } finally {
      setDeleting(null);
    }
  };

  const handleUploadBackup = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.zip')) {
      toast.error('Please select a ZIP file');
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    setUploading(true);
    try {
      const response = await axios.post(`${API}/admin/backups/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Backup uploaded and restored successfully!');
      fetchBackups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload and restore backup');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    try {
      return new Date(dateStr).toLocaleString('en-PH', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getTotalRecords = (collections) => {
    if (!collections) return 0;
    return Object.values(collections).reduce((sum, val) => {
      return sum + (typeof val === 'number' ? val : 0);
    }, 0);
  };

  // Only admin can access
  if (user?.role !== 'admin') {
    return (
      <div className="p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 text-red-700">
              <AlertTriangle className="h-6 w-6" />
              <div>
                <h3 className="font-semibold">Access Denied</h3>
                <p className="text-sm">Only administrators can access the Backup & Restore feature.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="backup-restore-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="h-6 w-6" />
            Backup & Restore
          </h1>
          <p className="text-gray-500 mt-1">Manage your data backups securely</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleCreateBackup}
            disabled={creating}
            className="bg-green-600 hover:bg-green-700"
            data-testid="create-backup-btn"
          >
            {creating ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            Create Backup
          </Button>
          <label>
            <input
              type="file"
              accept=".zip"
              onChange={handleUploadBackup}
              className="hidden"
              disabled={uploading}
            />
            <Button
              variant="outline"
              disabled={uploading}
              asChild
              data-testid="upload-backup-btn"
            >
              <span className="cursor-pointer">
                {uploading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                Upload & Restore
              </span>
            </Button>
          </label>
        </div>
      </div>

      {/* Warning Card */}
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-semibold">Important Notes:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li><strong>Create Backup</strong> - Saves all current data to a backup file</li>
                <li><strong>Download</strong> - Downloads backup as ZIP file (save this securely!)</li>
                <li><strong>Restore</strong> - <span className="text-red-600 font-semibold">OVERWRITES all current data</span> with backup data</li>
                <li><strong>Upload & Restore</strong> - Upload a previously downloaded backup ZIP to restore</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Backups List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Available Backups
          </CardTitle>
          <CardDescription>
            {backups.length} backup{backups.length !== 1 ? 's' : ''} available
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : backups.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <HardDrive className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No backups found</p>
              <p className="text-sm">Create your first backup to protect your data</p>
            </div>
          ) : (
            <div className="space-y-3">
              {backups.map((backup) => (
                <div 
                  key={backup.id} 
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  data-testid={`backup-item-${backup.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold">{backup.id}</h4>
                        <Badge variant="outline" className="text-xs">
                          {getTotalRecords(backup.collections)} records
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Created: {formatDate(backup.date)}
                      </p>
                      {backup.collections && Object.keys(backup.collections).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {Object.entries(backup.collections).map(([key, value]) => (
                            typeof value === 'number' && value > 0 && (
                              <span key={key} className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                                {key}: {value}
                              </span>
                            )
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownloadBackup(backup.id)}
                        data-testid={`download-${backup.id}`}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        onClick={() => {
                          setSelectedBackup(backup);
                          setShowRestoreDialog(true);
                        }}
                        data-testid={`restore-${backup.id}`}
                      >
                        <RefreshCw className="h-4 w-4 mr-1" />
                        Restore
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => {
                          setSelectedBackup(backup);
                          setShowDeleteDialog(true);
                        }}
                        data-testid={`delete-${backup.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Restore Confirmation Dialog */}
      <Dialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm Restore
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to restore from <strong>{selectedBackup?.id}</strong>?
            </DialogDescription>
          </DialogHeader>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4">
            <p className="text-red-700 font-semibold text-sm">
              ⚠️ WARNING: This will OVERWRITE all current data!
            </p>
            <p className="text-red-600 text-sm mt-1">
              All existing subscribers, payments, invoices, timesheets, and other data will be replaced with the backup data.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRestoreDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleRestoreBackup}
              disabled={restoring}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {restoring ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Yes, Restore Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Backup
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{selectedBackup?.id}</strong>?
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            This action cannot be undone. The backup file will be permanently deleted.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteBackup}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Delete Backup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BackupRestore;
